<template>
    <ContentLayout>
        <!-- Page header -->
        <header class="mb-6 sm:flex sm:items-center sm:justify-between">
            <!-- Left: Title -->
            <div class="mb-4 sm:mb-0">
                <Heading :level="1" title="Campaigns" />
            </div>

            <!-- Right: Actions  -->
            <div class="grid grid-flow-col justify-start gap-2 sm:auto-cols-max sm:justify-end">
                <template v-if="selfActiveOrganization && checkPermission('feature.campaigns.manage')">
                    <a
                        class="btn btn-sm bg-indigo-500 text-white hover:bg-indigo-600"
                        href="https://meetings.hubspot.com/streamforge/create-campaign"
                        target="_blank"
                        @click.prevent="$store.commit('modal:open', 'create_campaign')"
                    >
                        <AppIcon
                            name="plus"
                            class="h-[18px] w-[18px] shrink-0 text-slate-100"
                            stroke-width="2.5"
                        />
                        <span class="ml-2 hidden xl:block"> Create Campaign </span>
                    </a>

                    <hr class="h-8 w-px bg-slate-200" />
                </template>

                <!-- Search form -->
                <div class="relative">
                    <label for="action-search" class="sr-only">Search</label>
                    <input
                        v-model="state.searchText"
                        class="form-input pl-9 focus:border-slate-300"
                        type="search"
                        placeholder="Search campaigns..."
                    />
                    <div class="group absolute inset-0 right-auto" aria-label="Search">
                        <svg
                            class="ml-3 mr-2 mt-2.5 h-4 w-4 shrink-0 fill-current text-slate-400 group-hover:text-slate-500"
                            viewBox="0 0 16 16"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M7 14c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7zM7 2C4.243 2 2 4.243 2 7s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"
                            />
                            <path
                                d="M15.707 14.293L13.314 11.9a8.019 8.019 0 01-1.414 1.414l2.393 2.393a.997.997 0 001.414 0 .999.999 0 000-1.414z"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </header>

        <!-- Cards -->
        <div v-if="!state.isLoaded" class="grid grid-cols-12 gap-6">
            <SkeletonSupercampaignCard v-for="index in 8" :key="index" />
        </div>

        <div v-else-if="values.supercampaigns && values.supercampaigns.length" class="grid grid-cols-12 gap-6">
            <SupercampaignCard
                v-for="supercampaign in visibleSupercampaigns"
                :key="supercampaign.id"
                :supercampaign="supercampaign"
            />
        </div>

        <div
            v-if="
                state.isLoaded &&
                !visibleSupercampaigns.length &&
                values.supercampaigns !== 0 &&
                state.searchText &&
                state.searchText.length > 0
            "
            class="mt-4 text-center"
        >
            <h1 class="text-lg font-semibold">No Campaigns Found Matching "{{ state.searchText }}"</h1>
        </div>

        <NoResults
            v-if="state.isLoaded && (!values.supercampaigns || !values.supercampaigns.length)"
            title="Create a campaign"
            message="Looks like you don't have any campaigns yet! Please reach out to your account manager or contact the Streamforge team using the chat bubble in the bottom right to get one started!"
        >
            <template #icon>
                <div class="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-t from-slate-200 to-slate-100">
                    <svg class="h-6 w-5 fill-current text-slate-500" viewBox="0 0 20 24">
                        <path class="text-slate-500" d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z" />
                        <path class="text-slate-300" d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z" />
                        <path class="text-slate-400" d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z" />
                    </svg>
                </div>
            </template>
        </NoResults>
    </ContentLayout>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { ContentLayout } from '@/components/ui/layouts'
import SupercampaignCard from '@/partials/cards/SupercampaignCard.vue'
import SkeletonSupercampaignCard from '@/partials/skeletons/SkeletonSupercampaignCard.vue'
import { NoResults } from '@/components/ui/empty-state'
import { Heading}  from '@/components/ui/typography'

export default {
    name: 'Campaigns',

    metaInfo: {
        title: 'Campaigns',
    },

    components: {
        ContentLayout,
        SupercampaignCard,
        SkeletonSupercampaignCard,
        NoResults,
        Heading,
    },

    data() {
        return {
            state: {
                isLoaded: false,
                searchText: '',
            },

            values: {
                supercampaigns: null,
                superviewsById: null,
            },
        }
    },

    computed: {
        ...mapGetters(['selfActiveOrganization', 'checkPermission']),

        visibleSupercampaigns() {
            if (!this.state.isLoaded || !this.values.supercampaigns || !this.values.supercampaigns.length) return []

            if (this.state.searchText) {
                return _.filter(
                    this.values.supercampaigns,
                    (supercampaign) =>
                        supercampaign.title &&
                        supercampaign.title.toLowerCase().includes(this.state.searchText.toLowerCase()),
                )
            }

            return this.values.supercampaigns
        },
    },

    created() {
        this.getSupercampaigns()
    },

    methods: {
        getSupercampaigns() {
            this.$axios
                .get('/supercampaigns', {
                    params: {
                        relations: ['games', 'campaigns', 'participants.contracted'],
                    },
                })
                .then(({ data }) => {
                    this.values.supercampaigns = _(data.payload.supercampaigns)
                        .orderBy(['created_at'], ['desc'])
                        .value()

                    this.state.isLoaded = true
                })
                .catch((error) => {
                    if (error.is_cancelled) return

                    this.$errorHandler(error, {
                        title: 'Error - Campaigns',
                        message: 'Sorry, there was an issue retrieving your campaigns.',
                    })
                })
        },
    },
}
</script>
